import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";
import {
  Building2,
  FileText,
  Globe,
  Network,
  Hash,
  X,
  Save,
} from "lucide-react";

export default function UpdateDepartmentModal({
  isOpen,
  onClose,
  department,
  onSuccess,
}) {
  const [formData, setFormData] = useState({
    departmentName: "",
    departmentSlug: "",
    iPv4Address: "",
    vlanId: "",
    hotspotName: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (department) {
      setFormData({
        departmentName: department.departmentName || "",
        departmentSlug: department.departmentSlug || "",
        iPv4Address: department.iPv4Address || "",
        vlanId: department.vlanId || "",
        hotspotName: department.hotspotName || "",
      });
    }
  }, [department]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === "departmentName") {
        newData.departmentSlug = value.toLowerCase().replace(/\s+/g, "-");
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("dbToken");
      const payload = {
        departmentName: formData.departmentName,
        departmentSlug: formData.departmentSlug,
        iPv4Address: formData.iPv4Address,
        hotspotName: formData.hotspotName,
        vlanId: Number(formData.vlanId), // Ensure it's a number
      };

      await axios.patch(`${server}/department/UpdateById`, payload, {
        params: {
          departmentId: department.id,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Department updated successfully!");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error updating department:", error);
      toast.error(
        error.response?.data?.message || "Failed to update department",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Update Department
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Modify department configuration details
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* content */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Department Name */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                  Department Name
                </label>
                <div className="relative group">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                  <input
                    type="text"
                    name="departmentName"
                    value={formData.departmentName}
                    onChange={handleChange}
                    placeholder="e.g. Finance"
                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                    required
                  />
                </div>
              </div>

              {/* Department Slug */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                  Department Slug
                </label>
                <div className="relative group">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                  <input
                    type="text"
                    name="departmentSlug"
                    value={formData.departmentSlug}
                    onChange={handleChange}
                    placeholder="auto-generated-slug"
                    className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                    required
                    readOnly
                  />
                </div>
              </div>

              {/* IPv4 Address */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                  IPv4 Address
                </label>
                <div className="relative group">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                  <input
                    type="text"
                    name="iPv4Address"
                    value={formData.iPv4Address}
                    onChange={handleChange}
                    placeholder="e.g. 10.10.100.0/24"
                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                    required
                  />
                </div>
              </div>

              {/* VLAN ID */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                  VLAN ID
                </label>
                <div className="relative group">
                  <Network className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                  <input
                    type="number"
                    name="vlanId"
                    value={formData.vlanId}
                    onChange={handleChange}
                    placeholder="e.g. 100"
                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                    required
                  />
                </div>
              </div>

              {/* Hotspot Name */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                  Hotspot Name
                </label>
                <div className="relative group">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                  <input
                    type="text"
                    name="hotspotName"
                    value={formData.hotspotName}
                    onChange={handleChange}
                    placeholder="e.g. Hotspot-1"
                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium text-white shadow-sm transition-all ${
                  loading
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                    : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 hover:shadow"
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
